import { reportErrorToSentry } from "@aix/webutils/src/sentry"
import {
  Button,
  buttonVariants,
  cn,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  useScreenSize,
} from "@aix/ui"
import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowUpRight03Icon } from "@hugeicons-pro/core-solid-rounded"
import { HugeiconsIcon } from "@hugeicons/react"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { isEmpty } from "lodash-es"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import z from "zod"
import { Logo } from "~/components/Logo"
import { useAuthSession } from "~/hooks/useAuthSession"
import { api } from "~/utils/api"

import type { FC } from "react"
import type { IUser } from "@aix/vidmax-shared/src/constants/prismaTypings"

const getLinkFormSchema = z.object({
  email: z.string().max(200).nonempty(),
  password: z.string().max(200),
})

const AdminSignInPage: FC = () => {
  const { breakpoint } = useScreenSize()
  const navigate = useNavigate()
  const { invite: inviteIdParam } = Route.useSearch()
  const search = useSearch({ from: "/admin/signin" })

  const [fetchedInvite, setFetchedInvite] = useState<{ User: IUser } | null>(null)
  const ctx = api.useContext()

  useEffect(() => {
    if (inviteIdParam) {
      const fetchInvite = async () => {
        try {
          const response = await ctx.teams.getInviteId.fetch({
            inviteId: inviteIdParam,
          })
          setFetchedInvite(response)
        } catch (error) {
          void reportErrorToSentry(error, "adminSignin.fetchInvite")
        }
      }

      void fetchInvite()
    }
  }, [inviteIdParam])

  const form = useForm<z.infer<typeof getLinkFormSchema>>({
    resolver: zodResolver(getLinkFormSchema),
    defaultValues: {
      email: "",
    },
  })

  useEffect(() => {
    if (!isEmpty(fetchedInvite)) {
      form.setValue("email", fetchedInvite?.User?.email ?? "")
    }
  }, [fetchedInvite])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>("")

  // Check for auth errors in URL parameters
  useEffect(() => {
    if (search.error) {
      if (search.error === "CredentialsSignin") {
        setErrorMessage("Invalid email or password. Please try again.")
      } else {
        setErrorMessage("Authentication failed. Please try again.")
      }

      // Clean up URL by navigating without error parameter
      void navigate({ to: "/admin/signin", replace: true })
    }
  }, [search.error, navigate])

  // Session
  const { session, signInWithCredentials } = useAuthSession()

  const onSubmit = async (values: z.infer<typeof getLinkFormSchema>) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setErrorMessage("") // Clear previous errors

    try {
      // Use form submission flow (redirect: true) for credentials
      // Errors will come back via URL parameters
      await signInWithCredentials(values.email, values.password, {
        callbackUrl: "/home",
      })

      // If we reach here, signIn initiated the form submission
      // Success/failure will be handled by the form redirect
    } catch (err) {
      void reportErrorToSentry(err, "adminSignin.onSubmit")
      setErrorMessage("An error occurred during sign in. Please try again.")
    }
    setIsSubmitting(false)
  }

  // If the user is already authenticated, redirect to the home page
  useEffect(() => {
    if (session?.user?.id) {
      void navigate({ to: "/home" })
    }
  }, [session])

  if (session?.user?.id) {
    return <></>
  }

  return (
    <section className="relative flex flex-col h-screen lg:flex-row">
      <div className={cn("z-10 flex flex-[2] flex-col items-center justify-center p-7 pt-0", "lg:p-20 lg:pt-0")}>
        <div className={cn("flex w-full items-center pt-5", "lg:pt-14")}>
          <Logo />
          {/* {breakpoint.lg && (
            <span className="flex-1 font-semibold text-right text-muted-foreground">
              Signin
            </span>
          )} */}
        </div>

        <div className="flex w-full max-w-[280px] flex-1 flex-col items-center justify-center lg:max-w-[350px]">
          <h1 className="pb-5 text-2xl font-bold">Welcome Admin</h1>

          <Form {...form}>
            <form className="flex flex-col w-full gap-y-3" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input type="email" placeholder="Enter your email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input type="password" placeholder="Enter your password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                Sign in with Email
              </Button>

              {errorMessage && (
                <div className="mt-3 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                  {errorMessage}
                </div>
              )}
            </form>
          </Form>
        </div>

        {breakpoint.lg && (
          <div className="flex">
            <a
              href="https://vidmax.ai/terms"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants({ variant: "ghost" }), "self-end bg-transparent text-xs")}
            >
              Terms of Use
            </a>

            <a
              href="https://vidmax.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants({ variant: "ghost" }), "self-end bg-transparent text-xs")}
            >
              Privacy
            </a>
          </div>
        )}
      </div>

      {!breakpoint.lg && (
        <div className="relative z-10 flex min-w-[480px] flex-1 shrink-0 flex-col items-center overflow-hidden bg-primary px-20 py-14">
          <div className="flex items-end w-full">
            {/* <span className="flex-1 font-semibold text-right text-primary-foreground">
              Signin
            </span> */}
          </div>

          <div className="flex w-full max-w-[420px] flex-1 flex-col justify-center">
            <h1 className="mb-4 max-w-[300px] text-center text-3xl font-bold text-primary-foreground lg:text-left">
              Join the Revolution in Content Creation.
            </h1>

            <p className="mb-6 max-w-[440px] text-center text-base font-light text-primary-foreground lg:text-left">
              Some of the most cutting edge creators are harnessing the power of VidMax AI to craft a warchest of
              engaging social posts. Be part of the community that&apos;s shaping the future of AI Content Creation.
            </p>

            <a
              href="https://vidmax.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                buttonVariants({ variant: "outline" }),
                "gap-1 self-end bg-transparent text-primary-foreground",
              )}
            >
              Learn More
              <HugeiconsIcon icon={ArrowUpRight03Icon} size={16} />
            </a>
          </div>

          <div className="flex w-full">
            <a
              href="https://vidmax.ai/terms"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                buttonVariants({ variant: "ghost" }),
                "gap-1 self-end bg-transparent text-xs text-primary-foreground",
              )}
            >
              Terms of Use
            </a>

            <a
              href="https://vidmax.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                buttonVariants({ variant: "ghost" }),
                "gap-1 self-end bg-transparent text-xs text-primary-foreground",
              )}
            >
              Privacy
            </a>
          </div>
        </div>
      )}
    </section>
  )
}

// Define the search schema for this route to handle auth errors
const adminSignInSearchSchema = z.object({
  error: z.string().optional(),
  invite: z.string().optional(),
})

export const Route = createFileRoute("/admin/signin")({
  validateSearch: adminSignInSearchSchema,
  component: AdminSignInPage,
})
